import { z } from 'zod';
import { RequestPriorityEnum, RequestTypeEnum } from './enums.js';

const attachmentKindSchema = z.enum(['ATTACHMENT', 'SCREENSHOT']);

const attachmentSensitivitySchema = z.enum(['NORMAL', 'SENSITIVE']);

/** At most this many pins on one capture. */
export const MAX_PINS_PER_ATTACHMENT = 20;

/** A pin's note, long enough for a sentence and short enough to render beside a marker. */
export const MAX_PIN_TEXT_LENGTH = 500;

/**
 * One marker a reporter dropped on a capture, and what they said about it.
 *
 * **Coordinates are normalized to 0..1**, not pixels. The same image is
 * rendered at three different widths (the widget's preview, the admin
 * lightbox, the client portal), and a pixel coordinate is true in exactly one
 * of them. Normalizing is what lets every surface place the marker by
 * multiplying by its own rendered size.
 *
 * `text` may be empty. A numbered marker with nothing typed against it still
 * says "look here", which is the cheapest useful thing a reporter can do, and
 * refusing it would mean silently discarding a pin they placed deliberately.
 */
const ingestPinSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  text: z.string().max(MAX_PIN_TEXT_LENGTH),
});

const ingestAttachmentSchema = z.object({
  blobKey: z.string().min(1),
  fileName: z.string().min(1),
  contentType: z.string().min(1),
  sizeBytes: z.number().int().nonnegative(),
  kind: attachmentKindSchema,
  /**
   * How closely these bytes should be held. Optional, defaulting to NORMAL
   * server-side, so every existing embed keeps working unchanged.
   *
   * A caller may only ESCALATE. `IngestService.resolveSensitivity` forces mail
   * formats to SENSITIVE whatever this says, because a caller is not the right
   * judge of whether its own upload is a person's correspondence — and the
   * product whose users consented to "we collect a screenshot" is the one most
   * likely to get that wrong by omission.
   */
  sensitivity: attachmentSensitivitySchema.optional(),
  /**
   * Markers dropped on THIS capture, in the order they were placed.
   *
   * Nested here rather than carried on the request, because a pin is a point
   * on a picture and has no meaning apart from the picture it points at.
   * Putting the list beside the request would leave the join to be guessed at
   * by whichever screen renders it (ADR 0030).
   */
  pins: z.array(ingestPinSchema).max(MAX_PINS_PER_ATTACHMENT).optional(),
});

/**
 * The fallback title for a report that carried no words to derive one from.
 *
 * Shared rather than restated at each end, because it becomes the DevStride
 * work item's NAME — the artifact Tenger staff actually work from — and the
 * widget, the API and any direct caller must all produce the same one.
 */
export const UNTITLED_REQUEST_TITLE = 'Untitled report';

/**
 * The fields every intake caller may send, before any surface-specific rule.
 * Kept as a plain shape so the browser and server schemas below are built
 * from ONE list and cannot disagree about what a report is.
 */
const ingestRequestFields = {
  type: RequestTypeEnum,
  /**
   * Optional on the wire and defaulted server-side, NOT because a title is
   * unimportant but because it is DERIVED. The widget synthesises it from the
   * description's first line (`deriveTitle`), so a report with no description
   * has nothing to synthesise from, and a direct caller (a desktop reporter)
   * has no reason to invent one. `IngestService` substitutes
   * {@link UNTITLED_REQUEST_TITLE}; the column stays NOT NULL.
   */
  title: z.string().optional(),
  /**
   * Optional, because a SCREENSHOT-ONLY report is a legitimate shape: the
   * reporter grabs the screen and sends it. This was `min(1)`, which refused
   * that submission outright. The refinement below is what keeps an entirely
   * empty report illegal.
   */
  description: z.string().optional(),
  // Reporter-selected priority. Optional on the wire; the ingest service
  // defaults a missing value to NORMAL so older embeds keep working.
  priority: RequestPriorityEnum.optional(),
  metadata: z.record(z.any()).optional(),
  reporterName: z.string().optional(),
  reporterEmail: z.string().email().optional(),
  reporterUserId: z.string().optional(),
  attachments: z.array(ingestAttachmentSchema).optional(),
};

/**
 * Relaxing `description` must not make an EMPTY submission legal. A report has
 * to carry something a triager can act on: words, or a file. This is the guard
 * that used to be a side effect of `description.min(1)`, now stated as the rule
 * it always was. Shared by both intake schemas.
 */
function requireSomethingToActOn(
  value: { description?: string; attachments?: unknown[] },
  ctx: z.RefinementCtx,
): void {
  const hasWords = (value.description ?? '').trim().length > 0;
  const hasFile = (value.attachments ?? []).length > 0;
  if (!hasWords && !hasFile) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['description'],
      message: 'A request needs a description or at least one attachment.',
    });
  }
}

/** What the public key is told when it tries to route work (ADR 0038). */
export const ROUTING_REFUSED_ON_PUBLIC_KEY =
  'DevStride routing is accepted only from a server holding the backend key.';

/**
 * The browser-facing intake body (`POST /ingest/requests`, integration key).
 *
 * `routing` is named here only to REFUSE it. The schema is not strict, so an
 * unknown field would otherwise be silently stripped, and a widget, or anyone
 * holding the public key from a page's source, that tried to send work to
 * another assignee would get a 201 and a request that went somewhere else. A
 * 400 says plainly that the public key cannot do this (ADR 0038).
 */
export const ingestRequestSchema = z
  .object({
    ...ingestRequestFields,
    routing: z.never({ message: ROUTING_REFUSED_ON_PUBLIC_KEY }).optional(),
  })
  .superRefine(requireSomethingToActOn);

export type IngestRequest = z.infer<typeof ingestRequestSchema>;

/**
 * Per-request DevStride routing a trusted server may name (ADR 0038). Each
 * field overrides the matching value in the Application's DevStride settings
 * and an absent field keeps that value, so `{}` routes exactly as the
 * Application does.
 *
 * Identifiers, not display names, because they are what the Application's
 * settings store and what DevStride's create call takes: a username
 * (`user.username` on the membership), a team id, and a workstream number such
 * as `F845`. The server checks each against the live organization before
 * anything is written.
 */
export const devStrideRoutingSchema = z
  .object({
    assigneeUsername: z.string().trim().min(1).optional(),
    teamId: z.string().trim().min(1).optional(),
    workstreamNumber: z.string().trim().min(1).optional(),
  })
  .strict();

export type DevStrideRouting = z.infer<typeof devStrideRoutingSchema>;

/**
 * The server-to-server intake body (`POST /ingest/server/requests`, backend
 * key). The same report fields as the browser schema plus `routing`.
 *
 * **Strict**, unlike the browser schema. This surface is new, so nothing
 * depends on extra fields being dropped, and a server that misspells
 * `routing` should hear about it rather than file a request that lands on the
 * Application's default assignee without saying so.
 *
 * `title` carries no length cap here. The widget trims what it derives to 120
 * characters (`packages/widget/src/derive-title.ts`), which is a rule about
 * deriving a title from a description, not about titles: a server sends a
 * title it chose, and it reaches DevStride whole.
 */
export const serverIngestRequestSchema = z
  .object({
    ...ingestRequestFields,
    routing: devStrideRoutingSchema.optional(),
  })
  .strict()
  .superRefine(requireSomethingToActOn);

export type ServerIngestRequest = z.infer<typeof serverIngestRequestSchema>;
